import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, TextField, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, Chip, Paper, TableContainer, CircularProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import api from '../../api';

// Note: Removed docCategories from props as we will fetch it locally now
const ProfileDocuments = ({ empId, documents, setDocuments, showMsg, baseUrl }) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  
  // NEW: Local state to hold the filtered dropdown options
  const [availableCategories, setAvailableCategories] = useState([]);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await api.get(`/employees/${empId}/documents`);
      setDocuments(res.data || []);
    } catch (err) {
      console.error("Failed to refresh documents");
    }
  }, [empId, setDocuments]);

  // NEW: Function to fetch only the categories this employee is allowed to upload
  const fetchAvailableCategories = useCallback(async () => {
    try {
      const res = await api.get(`/myprofile/${empId}/available-document-categories`);
      setAvailableCategories(res.data || []);
    } catch (err) {
      console.error("Failed to fetch available categories");
    }
  }, [empId]);

  // Fetch both the table data and the dropdown options when the component loads
  useEffect(() => {
    if (empId) {
      fetchDocuments();
      fetchAvailableCategories();
    }
  }, [empId, fetchDocuments, fetchAvailableCategories]);

  const handleUpload = async () => {
    if (!uploadFile || !selectedCategory) return showMsg("Please select a category and file", "error");
    
    const formData = new FormData();
    formData.append("document_type", selectedCategory); 
    formData.append("file", uploadFile);

    const requiresValidation = selectedCategory.toUpperCase().includes('PAN') || selectedCategory.toUpperCase().includes('AADHAR');

    try {
      if (requiresValidation) {
        setIsScanning(true);
        await api.post(`/myprofile/${empId}/documents/verify-and-upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showMsg(`${selectedCategory} scanned, verified, and stored successfully!`, "success");
      } else {
        await api.post(`/employees/${empId}/documents`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showMsg("Document uploaded successfully!", "success");
      }
      
      setUploadFile(null);
      setSelectedCategory('');
      
      // Refresh BOTH the documents list AND the dropdown to instantly hide the uploaded category
      fetchDocuments();
      fetchAvailableCategories();

    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Validation failed. Please ensure the document is clear and valid.";
      showMsg(errorMsg, "error");
    } finally {
      setIsScanning(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', mb: 3, borderBottom: '2px solid #E2E8F0', pb: 1.5 }}>
        My Documents
      </Typography>
      
      <Box sx={{ 
        p: 2.5, mb: 3.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px dashed #CBD5E1', 
        display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' 
      }}>
        <TextField 
          select size="small" label="Document Category" 
          value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} 
          sx={{ minWidth: 200, bgcolor: 'white' }}
          disabled={isScanning}
        >
          {/* Map over the new local state instead of the old prop */}
          {availableCategories.length === 0 ? (
             <MenuItem value="" disabled>No pending documents required</MenuItem>
          ) : (
             availableCategories.map(cat => <MenuItem key={cat.id} value={cat.name}>{cat.name}</MenuItem>)
          )}
        </TextField>
        
        <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} sx={{ bgcolor: 'white' }} disabled={isScanning}>
          {uploadFile ? uploadFile.name : "Choose File"}
          <input type="file" hidden accept="image/*,.pdf" onChange={e => setUploadFile(e.target.files[0])} />
        </Button>
        
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleUpload} 
          disableElevation
          disabled={isScanning}
          startIcon={isScanning ? <CircularProgress size={20} color="inherit" /> : (selectedCategory.toUpperCase().includes('PAN') || selectedCategory.toUpperCase().includes('AADHAR') ? <DocumentScannerIcon /> : <CloudUploadIcon />)}
        >
          {isScanning ? "Scanning & Verifying..." : "Upload & Validate"}
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>Document Type</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>Upload Date</TableCell>
              <TableCell sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>Status</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.75rem' }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>No documents uploaded yet.</TableCell>
              </TableRow>
            ) : (
              documents.map(doc => (
                <TableRow key={doc.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{doc.document_type || doc.category}</TableCell>
                  <TableCell>{new Date(doc.upload_date || new Date()).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip size="small" label={doc.status} color={doc.status === 'APPROVED' ? 'success' : (doc.status === 'REJECTED' ? 'error' : 'warning')} sx={{ fontWeight: 'bold', fontSize: '0.7rem' }} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<DownloadIcon />} onClick={() => window.open(`${baseUrl}/employees/documents/${doc.id}/download`, '_blank')}>View</Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProfileDocuments;